import { FormGroup, FormParagraph } from '@ksyos/design-system';
import type { IOrganizationGeneralSettings } from '@common/validation/organization';
import type { ValidationErrors } from '@frontend/utils/validation';
import { SelectCountry } from '@frontend/components/elements/SelectCountry';
import { SelectState } from '@frontend/components/elements/SelectState';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';

type CountryStateFormGroupProps = {
    canEdit: boolean;
    errors: ValidationErrors;
    onChange: <Key extends keyof Pick<IOrganizationGeneralSettings, 'state' | 'country'>>(
        key: Key,
        value: IOrganizationGeneralSettings[Key]
    ) => void;
    organization: IOrganizationGeneralSettings;
    validateOne: (name: keyof Pick<IOrganizationGeneralSettings, 'state' | 'country'>) => Promise<void>;
};

export function CountryStateFormGroup({
    canEdit,
    errors,
    onChange,
    organization: { country, state },
    validateOne,
}: Readonly<CountryStateFormGroupProps>) {
    const translate = getSelectedLanguage();

    return (
        <>
            <FormGroup
                hasErrors={errors.has('state')}
                isRequired={canEdit && country === 'NL'}
                label={translate.common.state}
            >
                {!canEdit && <FormParagraph>{state ?? '-'}</FormParagraph>}

                {canEdit && (
                    <SelectState
                        country={country}
                        error={getValidationErrorTranslation(errors.get('state'))}
                        onChange={async (newState) => onChange('state', newState)}
                        onDoValidate={async () => validateOne('state')}
                        value={state}
                    />
                )}
            </FormGroup>

            <FormGroup
                hasErrors={errors.has('country')}
                label={translate.common.country}
            >
                {!canEdit && (
                    <FormParagraph>
                        {Object.entries(translate.Countries).find(([key]) => key === country)?.[1] ?? '-'}
                    </FormParagraph>
                )}

                {canEdit && (
                    <SelectCountry
                        error={errors.get('country')}
                        onChange={async (newCountry) => onChange('country', newCountry)}
                        onDoValidate={async () => validateOne('country')}
                        value={country ?? undefined}
                    />
                )}
            </FormGroup>
        </>
    );
}
